/**
 * H22: sect activities and manual pages (data/sectActivities.ts). `sectAct` starts a patrol / spy hunt in your own sect map or
 * a spy mission in another's; `sectBind` sews 10/10 pages into a book; `sectRead` learns a held book by the normal learn path.
 * Daily counts live in scene marks `sectact:<day>:<kind>:<n>` (they survive the zone reload) and reset when `day` changes.
 */
import {
  SECT_ACT,
  SECT_ACT_NAME,
  bookById,
  bookGoodId,
  booksOfSect,
  pageId,
  type SectActKind,
} from '../data/sectActivities';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';
import { playerSect, zoneSect } from './sectJoin';

export type SectActCommand =
  | { t: 'sectAct'; kind: SectActKind }
  | { t: 'sectBind'; book: string }
  | { t: 'sectRead'; book: string };

interface Run {
  kind: SectActKind;
  sect: string;
  ox: number;
  oy: number;
  next: number;
  foes: Set<number>;
  guards: number[];
  caught: boolean;
}

export class SectActivitiesSystem implements WorldSystem {
  readonly name = 'sectActivities';
  /** The real day (set from dayReset's `realDay`). */
  day = 0;
  realDay(day: number): void {
    this.day = day;
  }
  run: Run | null = null;

  constructor(private readonly h: SystemHost) {}

  private get pr() {
    return this.h.world.progress;
  }
  private goods(): Record<string, number> {
    return this.pr.trades.goods;
  }
  private mySect(): string | null {
    return playerSect(this.pr);
  }
  private zoneSect(): string | null {
    return zoneSect(this.h.world.map.zone as string);
  }

  count(kind: SectActKind): number {
    const pre = `sectact:${this.day}:${kind}:`;
    const m = this.pr.scenes.find((s) => s.startsWith(pre));
    return m ? Number(m.slice(pre.length)) : 0;
  }
  private bump(kind: SectActKind): void {
    const sc = this.pr.scenes;
    const keep = sc.filter((s) => !s.startsWith('sectact:') || s.startsWith(`sectact:${this.day}:`));
    const pre = `sectact:${this.day}:${kind}:`;
    const n = this.count(kind) + 1;
    sc.splice(0, sc.length, ...keep.filter((s) => !s.startsWith(pre)), pre + n);
  }

  /** Pays `n` random pages of `sect`'s books. */
  dropPages(sect: string, n: number): void {
    const books = booksOfSect(sect);
    if (!books.length) return;
    const g = this.goods();
    for (let i = 0; i < n; i++) {
      const b = books[Math.floor(this.h.random() * books.length)]!;
      const id = pageId(b.id, 1 + Math.floor(this.h.random() * b.pageCount));
      g[id] = (g[id] ?? 0) + 1;
      this.h.notice(`ได้รับหน้าตำรา: ${b.name}`);
    }
  }

  pagesHeld(book: string): number {
    const b = bookById(book);
    if (!b) return 0;
    let n = 0;
    for (let i = 1; i <= b.pageCount; i++) if ((this.goods()[pageId(book, i)] ?? 0) > 0) n++;
    return n;
  }

  bind(book: string): boolean {
    const b = bookById(book);
    if (!b || this.pagesHeld(book) < b.pageCount) return false;
    const g = this.goods();
    for (let i = 1; i <= b.pageCount; i++) {
      const id = pageId(book, i);
      if (--g[id]! <= 0) delete g[id];
    }
    const bid = bookGoodId(book);
    g[bid] = (g[bid] ?? 0) + 1;
    this.h.notice(`รวมเล่มสำเร็จ: ${b.name}`);
    return true;
  }

  private foe(id: string, x: number, y: number): Entity {
    return this.h.spawnMonster(id, x, y);
  }

  start(kind: SectActKind): boolean {
    const w = this.h.world;
    const p = w.player;
    if (this.run || p.dead) return false;
    const here = this.zoneSect();
    const mine = this.mySect();
    if (!here || !mine) { this.h.notice('ต้องอยู่ในแผนที่สำนักและเป็นศิษย์สำนัก'); return false; }
    if (kind === 'spy' ? here === mine : here !== mine) { this.h.notice(kind === 'spy' ? 'ต้องไปสำนักอื่น' : 'ต้องอยู่สำนักของตน'); return false; }
    if (this.count(kind) >= SECT_ACT.daily[kind]) { this.h.notice(`${SECT_ACT_NAME[kind]} ครบจำนวนวันนี้แล้ว`); return false; }
    const r: Run = { kind, sect: here, ox: 0, oy: 0, next: 0, foes: new Set(), guards: [], caught: false };
    if (kind === 'patrol') {
      const roll = this.h.random();
      if (roll < SECT_ACT.intruderChance) {
        const q = SECT_ACT.intruderSpot;
        r.foes.add(this.foe(SECT_ACT.monster, q.x, q.y).id);
      }
    } else if (kind === 'hunt') {
      for (let i = 0; i < SECT_ACT.huntSpies; i++) {
        const q = SECT_ACT.huntSpots[i % SECT_ACT.huntSpots.length]!;
        r.foes.add(this.foe(SECT_ACT.monster, q.x, q.y).id);
      }
    } else {
      for (const g of SECT_ACT.guards) r.guards.push(this.foe(SECT_ACT.monster, g.x, g.y).id);
    }
    this.run = r;
    this.h.notice(`เริ่ม${SECT_ACT_NAME[kind]}`);
    return true;
  }

  private finish(ok: boolean): void {
    const r = this.run!;
    this.run = null;
    if (!ok) return void this.h.notice(`${SECT_ACT_NAME[r.kind]}ล้มเหลว`);
    this.bump(r.kind);
    const mine = this.mySect()!;
    this.dropPages(r.kind === 'spy' ? r.sect : mine, SECT_ACT.pages[r.kind]);
    this.h.notice(`${SECT_ACT_NAME[r.kind]}สำเร็จ`);
  }

  /** UI read: progress of the active run. */
  hud(): { kind: SectActKind; next: number; total: number; foes: number; caught: boolean } | null {
    const r = this.run;
    if (!r) return null;
    const total = r.kind === 'patrol' ? SECT_ACT.patrolPoints.length : r.kind === 'spy' ? SECT_ACT.spyPoints.length : 0;
    return { kind: r.kind, next: r.next, total, foes: r.foes.size, caught: r.caught };
  }

  readonly commands = {
    sectAct: (cmd: Extract<SectActCommand, { t: 'sectAct' }>) => void this.start(cmd.kind),
    sectBind: (cmd: Extract<SectActCommand, { t: 'sectBind' }>) => void this.bind(cmd.book),
    sectRead: (cmd: Extract<SectActCommand, { t: 'sectRead' }>) => {
      const b = bookById(cmd.book);
      const g = this.goods();
      const bid = bookGoodId(cmd.book);
      if (!b || (g[bid] ?? 0) < 1) return;
      const pr = this.pr;
      const before = b.kind === 'art' ? pr.arts.includes(b.target) : pr.neigong[b.target] != null;
      if (before) return void this.h.notice('เรียนแล้ว');
      this.h.world.apply(b.kind === 'art' ? { t: 'learnArt', id: b.target } : { t: 'learnNeigong', id: b.target });
      const after = b.kind === 'art' ? pr.arts.includes(b.target) : pr.neigong[b.target] != null;
      if (after && --g[bid]! <= 0) delete g[bid];
    },
  };

  step(_dt: number): void {
    const r = this.run;
    if (!r) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead || this.zoneSect() !== r.sect) return this.finish(false);
    if (r.kind === 'spy') {
      if (!r.caught) {
        for (const id of r.guards) {
          const e = w.entities.get(id);
          if (e && !e.dead && Math.hypot(e.x - p.x, e.y - p.y) <= SECT_ACT.detectRadius) {
            r.caught = true;
            this.h.notice('ถูกยามจับได้! หนีไปทางออก');
          }
        }
      }
      if (r.caught)
        for (const id of r.guards) {
          const e = w.entities.get(id);
          if (e?.brain && !e.dead) e.brain.state = 'chase';
        }
    }
    const pts = r.kind === 'patrol' ? SECT_ACT.patrolPoints : r.kind === 'spy' ? SECT_ACT.spyPoints : [];
    if (r.next < pts.length) {
      const q = pts[r.next]!;
      if (Math.hypot(r.ox + q.x - p.x, r.oy + q.y - p.y) <= SECT_ACT.reach) {
        r.next++;
        this.h.notice(`จุดที่ ${r.next}/${pts.length}`);
      }
    }
    if (r.kind === 'hunt' ? r.foes.size === 0 : r.next >= pts.length && r.foes.size === 0) this.finish(true);
  }

  observe(ev: WorldEvent): void {
    const r = this.run;
    if (r && ev.type === 'death') r.foes.delete(ev.id);
  }
}
